import React, { useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import Button from '../../components/Button';
import FooterWrapper from '../../components/FooterWrapper';

import { ScheduleContext } from '../../contexts/ScheduleContext';
import { PAYMENT, Payment } from '../../models/Schedule';

import * as S from './styles';
import { PaymentMethod } from './components/PaymentMethod';
import {
  INFO_GENERIC_RULES_ACTION,
  INFO_GENERIC_RULES_CONDITION,
  INFO_GENERIC_RULES_WHEN,
  PAYMENT_TYPE
} from '../../utils/constants';
import { CartContext } from '../../contexts/CartContext';
import { ModalGenericRules } from '../../components/ModalGenericRules';
import LoadingOverlay from '../../components/LoadingOverlay';
import SystemUnavailable from '../../components/SystemUnavailable';
import { ModalProductNotCovered } from '../../components/ModalProductNotCovered';
import { usePatientModule } from '../../hooks/usePatientModule';
import { ProductItemProps } from '../../models/Product';
import { PatientContext, PatientProps } from '../../contexts/PatientContext';
import { ModuleContext } from '../../contexts/ModuleContext';
import ModalNoEligibility from '../../components/ModalNoEligibility';
import { PAGES } from '../../Router/pages';
import { RouterContext } from '../../contexts/RouterContext';
import { PaymentContext } from '../../contexts/PaymentContext';
import { ModalsContext } from '../../contexts/ModalsContext';
import { fetchSpecialistMedicalCare } from '../../modules/specialists';
import { toast } from 'react-toastify';
import { UnitHealthPlans } from '../../models/Specialist';
import { HealthCarePaymentModal } from '../../flows/Specialist/components/HealthCarePaymentModal';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FirebaseEvents } from '../../utils/enums/firebase-events';
import { useServiceContext } from '../../contexts/ServiceContext';

const PaymentView = () => {
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { changeProduct } = useContextSelector(CartContext, state => state);
  const { findProductOriginal } = useContextSelector(CartContext, state => state);
  const { patientSelected, addPatientList } = useContextSelector(PatientContext, state => state);
  const { selectedProduct, selectedItemToSchedule } = useContextSelector(ModuleContext, state => state);
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { logEvent } = useAnalytics();
  const {
    selectedItem,
    changeSchedule,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  } = useContextSelector(ScheduleContext, state => state);
  const {
    createSchedule,
    genericModal,
    isModalSystemUnavailableVisible,
    setIsModalSystemUnavailableVisible,
    isModalProductNotCoveredVisible,
    setIsModalProductNotCoveredVisible,
    isModalNoEligibility,
    setIsModalNoEligibility
  } = usePatientModule({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    goTo,
    changeSchedule,
    selectedSchedule: selectedItem,
    openModal,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  });
  const productSelectedPayment = selectedItem?.payment;
  const [payment, setPayment] = React.useState<Payment | undefined>(productSelectedPayment);
  const [loading, setLoading] = useState(false);
  const [specialistMedicalCare, setSpecialistMedicalCare] = React.useState<UnitHealthPlans | null>(null);
  const [isModalHealthInsuranceUnavailableVisible, setIsModalHealthInsuranceUnavailableVisible] = React.useState(false);
  const { selectedService } = useServiceContext();

  const handleClickPaymentMethod = (plan: string | Payment) => {
    if (typeof plan === 'string') {
      setPayment({ ...payment, type: plan });
    } else {
      setPayment(plan);
    }
  };

  useEffect(() => {
    getSpecialistHelthPlans();
  }, []);

  const getSpecialistHelthPlans = async () => {
    setLoading(true);
    const result = await fetchSpecialistMedicalCare({
      officeId: scheduleSpecialistSelected.unit.officeId,
      specialistId: scheduleSpecialistSelected.id
    });

    setLoading(false);
    if (result.error) return toast.error('Não foi possível obter os dados de pagamento.');

    setSpecialistMedicalCare(result.data);
  };

  const handleContinue = async () => {
    if (!specialistMedicalCare) return;

    if (selectedItemToSchedule === 'SPECIALIST') {
      if (!payment) return;
      interface TypeName {
        1: string;
        2: string;
        3: string;
      }
      const typeIndex = payment.type || null;
      if (typeIndex) {
        const type = {
          1: 'Particular',
          2: 'B2B',
          3: 'Convênio'
        }[typeIndex];

        logEvent({
          eventName: FirebaseEvents.schedule_payment_option,
          eventParams: {
            product_id: selectedItem?.productId ?? '',
            product_name: selectedItem?.productName ?? '',
            service_id: selectedService?.id ?? '',
            service_name: 'Médicos Einstein',
            type
          }
        });
      }
      const healthPlan = specialistMedicalCare.health_plans.find(
        item =>
          item.health_plan.description ===
          (payment.type === PAYMENT.PARTICULAR ? 'PARTICULAR' : payment?.healthPlan?.name)
      );

      if (payment?.type === PAYMENT.PARTICULAR) {
        delete payment.healthInsurance;
        delete payment.healthPlan;
      }

      payment.price = healthPlan?.price || '';
      const return_time = healthPlan?.return_time || '';

      changeScheduleSpecialist({
        ...scheduleSpecialistSelected,
        payment,
        return_time
      });

      goTo(PAGES.APPOINTMENT_DETAILS.id);
    } else {
      if (selectedItem?.name && payment) {
        const scheduleSelected = selectedItem;
        const productSelected = findProductOriginal(scheduleSelected?.productId || '');
        if (productSelected && patientSelected) {
          await routeProductSelector({
            patient: patientSelected,
            product: productSelected,
            payment: payment
          });
        }
      }
      if (payment?.type)
        if (selectedProduct && patientSelected) {
          await routeProductSelector({
            patient: patientSelected,
            product: selectedProduct,
            payment: payment
          });
        }
    }
  };

  const routeProductSelector = async ({
    product,
    patient,
    payment
  }: {
    product: ProductItemProps;
    patient: PatientProps;
    payment: Payment;
  }) => {
    setLoading(true);
    const hasModal = await createSchedule({
      patient: patient,
      product: product,
      itemToSchedule: 'PRODUCT',
      payment
    });
    if (hasModal?.type === INFO_GENERIC_RULES_CONDITION.is_first_appointment) {
      genericModal.setGenericModalCondition(INFO_GENERIC_RULES_CONDITION.is_first_appointment);
      genericModal.setModalGenericVisible(true);
    }
    setLoading(false);
  };

  async function handleGenericModalActionButton(actionButton: string, data: any) {
    if (actionButton === INFO_GENERIC_RULES_ACTION.CLOSE_MODAL) genericModal.setModalGenericVisible(false);
    if (actionButton === INFO_GENERIC_RULES_ACTION.OPEN_MODAL_CONDITION) genericModal.setGenericModalCondition(data);
    if (actionButton === INFO_GENERIC_RULES_ACTION.TO_SERVICE_TYPE) {
      genericModal.setModalGenericVisible(false);
      if (selectedProduct && patientSelected)
        if (genericModal.genericModalCondition === INFO_GENERIC_RULES_CONDITION.is_first_appointment) {
          goTo(PAGES.SERVICE_TYPE_PAGE.id);
        } else {
          if (payment)
            await routeProductSelector({
              product: selectedProduct,
              patient: patientSelected,
              payment
            });
        }
    }
  }

  const handleDisabledButton = () => {
    if (payment?.type) {
      if (payment.type === PAYMENT.HEALTH_INSURANCE && payment.healthInsurance?.name && payment.healthPlan?.name)
        return false;

      if (payment.type === PAYMENT.PARTICULAR) return false;
      /* if (payment.type === PAYMENT_TYPE.B2B) {
        return (
          !payment?.eligibility?.codPlan ||
          !payment?.eligibility?.idInsurance ||
          !payment?.eligibility?.idCustomer ||
          !payment?.eligibility?.plan
        );
      } */
    }
    return true;
  };

  return (
    <S.Container>
      <PaymentMethod
        payment={payment}
        setPayment={setPayment}
        specialistMedicalCare={specialistMedicalCare}
        handlePayment={paymentMethod => handleClickPaymentMethod(paymentMethod)}
        setIsModalHealthInsuranceUnavailableVisible={setIsModalHealthInsuranceUnavailableVisible}
      />
      <FooterWrapper>
        <Button disabled={handleDisabledButton()} title="Continuar" onClick={handleContinue} />
      </FooterWrapper>
      <ModalGenericRules
        when={INFO_GENERIC_RULES_WHEN.after_patient_identification}
        condition={genericModal.genericModalCondition}
        visible={genericModal.modalGenericVisible}
        isVisible={genericModal.setModalGenericVisible}
        handleActionButton={handleGenericModalActionButton}
      />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />
      <LoadingOverlay loading={loading} />
      <ModalProductNotCovered
        visible={isModalProductNotCoveredVisible}
        onClose={() => setIsModalProductNotCoveredVisible(false)}
      />
      <ModalNoEligibility
        visible={isModalNoEligibility}
        onClose={() => {
          setIsModalNoEligibility(false);
        }}
      />
      <HealthCarePaymentModal
        visible={isModalHealthInsuranceUnavailableVisible}
        onClose={() => setIsModalHealthInsuranceUnavailableVisible(false)}
      />
    </S.Container>
  );
};

export default PaymentView;
